/**
 * Modal picker for adding a saved query to an article.
 *
 * Lists every `save_type='public'` saved query (the backend enforces).
 * Substring search filters client-side over name / description / tags /
 * author. Clicking a row resolves the `onPick` callback with the
 * chosen entry and closes the dialog.
 *
 * Lazy: the list is fetched the first time the dialog opens, then
 * cached for subsequent opens in the same session. Keeps the editor
 * page snappy when an author doesn't need the picker.
 *
 * Already-embedded queries are NOT filtered out — same saved query can
 * legitimately appear twice in one article with different title
 * overrides. We just mark them visually so the author knows.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useLeagueApi } from "../../../../data/LeagueEnvironmentProvider";
import { SearchableSavedQuery } from "../../../../models/Research";

export interface QueryPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (savedQuery: SearchableSavedQuery) => void;
  /** saved_query_ids already embedded on this article — used to badge
   *  duplicates in the picker. Optional; default empty set. */
  alreadyEmbeddedIds?: Set<string>;
}

export default function QueryPickerDialog({
  open,
  onClose,
  onPick,
  alreadyEmbeddedIds,
}: QueryPickerDialogProps) {
  const api = useLeagueApi();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queries, setQueries] = useState<SearchableSavedQuery[]>([]);
  const [search, setSearch] = useState("");

  // Lazy-load on first open. Subsequent opens reuse the cached list —
  // the picker doesn't need to live-refresh during a single editor
  // session.
  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getSearchableSavedQueries()
      .then((resp) => {
        if (cancelled) return;
        setQueries(resp.data.saved_queries ?? []);
        setLoaded(true);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load saved queries";
        setError(msg);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, open, loaded]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return queries;
    return queries.filter((q) => {
      const hay = [q.name, q.description, q.tags, q.user_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [queries, search]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 0, minHeight: 480 },
      }}
    >
      {/* Dark header bar to match the Explorer modal aesthetic. */}
      <DialogTitle
        sx={{
          backgroundColor: "#4c4c4c",
          color: "#fff",
          textTransform: "uppercase",
          fontSize: "0.85rem",
          fontWeight: 700,
          letterSpacing: "0.5px",
          py: 1.5,
          pr: 5,
        }}
      >
        Pick a saved query
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#fff",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Search by name, description, tag, or author…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Box sx={{ maxHeight: 480, overflowY: "auto" }}>
          {loading && (
            <Typography
              sx={{ p: 3, color: "text.secondary", textAlign: "center" }}
            >
              Loading…
            </Typography>
          )}
          {error && (
            <Typography
              sx={{ p: 3, color: "#CF0A2C", textAlign: "center" }}
            >
              {error}
            </Typography>
          )}
          {!loading && !error && filtered.length === 0 && (
            <Typography
              sx={{ p: 3, color: "text.secondary", textAlign: "center" }}
            >
              {queries.length === 0
                ? "No public saved queries available yet."
                : "No queries match your search."}
            </Typography>
          )}
          {!loading &&
            !error &&
            filtered.map((q) => {
              const alreadyEmbedded =
                alreadyEmbeddedIds?.has(q.id) ?? false;
              return (
                <Box
                  key={q.id}
                  onClick={() => onPick(q)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    borderBottom: "1px solid #f0f0f0",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {q.name}
                    </Typography>
                    {alreadyEmbedded && (
                      <Chip
                        label="Already embedded"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          backgroundColor: "#e0e0e0",
                          color: "#555",
                        }}
                      />
                    )}
                  </Stack>
                  {q.description && (
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        color: "text.secondary",
                        mt: 0.25,
                        // 2-line clamp keeps long descriptions from
                        // overwhelming the picker list.
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {q.description}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      mt: 0.5,
                    }}
                  >
                    {q.user_name && <>By {q.user_name}</>}
                    {q.user_name && q.last_saved && " · "}
                    {q.last_saved && (
                      <>Saved {formatDate(q.last_saved)}</>
                    )}
                  </Typography>
                </Box>
              );
            })}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
